import React from 'react';
import { ShieldCheck, ShieldAlert, ShieldQuestion, ExternalLink } from 'lucide-react';

/**
 * Render the Smile ID advisory verification result inline on an admin
 * verification row. Designed to be terse and scannable — admins want to see
 * "Smile passed" or "Smile failed" at a glance, not a wall of metadata.
 *
 * Props:
 *   signal — the smile_signal object stored on the verification document:
 *     { status, confidence, provider_ref, raw: { kyc_receipt, result }, reason }
 *   For null / undefined / { skipped: true } signal → renders nothing.
 */
export default function SmileSignalBadge({ signal }) {
  if (!signal || signal.skipped) return null;
  const meta = pickMeta(signal);
  if (!meta) return null;
  const { Icon, label, tone, percent, receipt, code } = meta;
  return (
    <div
      className={`mt-1 flex items-center gap-1.5 text-xs ${tone}`}
      data-testid="smile-signal-badge"
      title={code ? `Smile ResultCode: ${code}` : undefined}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="font-medium">{label}</span>
      {percent !== null && (
        <span className="text-[11px] opacity-75">({percent}% confidence)</span>
      )}
      {receipt && (
        <a
          href={receipt}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 underline underline-offset-2 text-[11px] hover:opacity-80"
          data-testid="smile-signal-receipt-link"
        >
          receipt <ExternalLink className="w-2.5 h-2.5" />
        </a>
      )}
    </div>
  );
}

function pickMeta(signal) {
  const status = signal.status || 'unknown';
  const conf = typeof signal.confidence === 'number' ? Math.round(signal.confidence * 100) : null;
  const receipt = signal?.raw?.kyc_receipt || null;
  const code = signal?.raw?.result?.ResultCode || null;
  const tones = {
    approved: { Icon: ShieldCheck, label: 'Smile verified', tone: 'text-emerald-400' },
    rejected: { Icon: ShieldAlert, label: 'Smile rejected', tone: 'text-rose-400' },
    pending: { Icon: ShieldQuestion, label: 'Smile pending', tone: 'text-amber-400' },
    pending_pair: { Icon: ShieldQuestion, label: 'Smile awaiting selfie', tone: 'text-gray-400' },
    not_attempted: { Icon: ShieldQuestion, label: 'Smile unavailable', tone: 'text-gray-500' },
    not_configured: { Icon: ShieldQuestion, label: 'Smile unavailable', tone: 'text-gray-500' },
  };
  const fallback = { Icon: ShieldQuestion, label: `Smile: ${status}`, tone: 'text-gray-400' };
  const base = tones[status] || fallback;
  return { ...base, percent: conf, receipt, code };
}
